import {useCallback, useContext, useEffect, useMemo, useState} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {atomButtonDesigns, atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import {atomIconSizes} from '@s-ui/react-atom-icon'
import AtomToast, {atomToastAutoCloseTimes} from '@s-ui/react-atom-toast'
import Context from '@s-ui/react-context'
import {FeedbackInput} from '@adv-mt/frontend-ma--lib-trust'
import FormDealerRating from '@adv-mt/mt-form-dealer-rating'
import LayoutBasic from '@adv-mt/mt-layout-basic'
import IconAngleLeft from '@adv-mt/mt-svg-icons/lib/AngleLeft.js'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close.js'
import IconCloseCircle from '@adv-mt/mt-svg-icons/lib/CloseCircleOutline.js'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import LinkFactory from '../../components/LinkFactory/index.js'
import {ROUTES} from '../../config/index.js'
import EmptyRatingState from './components/emptyRatingState.js'
import {BASE_CLASS_NAME, getAnswers, getRatingStateConfig, RATING_STATES, URL_PARAMS} from './config.js'
import {getInitialProps} from './initialProps.js'

const Rating = ({adId, feedbackOnItem = {}, router, token, tradeId}) => {
  const {i18n, domain} = useContext(Context)

  const config = domain.get('config')
  const {TRACKING_EVENT_NAMES} = config.get('tracking')
  const {DEALER_RATING_STATE_PAGE_NAMES} = config.get('page')
  const urlStaticsImages = config.get('URL_STATICS_IMAGES')
  const TRUST_API_PATH = config.get('TRUST_API_PATH')
  const API_URL = config.get('API_URL')

  const {track, page} = useAnalyticsTrack()

  const [showErrorRating, setShowErrorRating] = useState(false)
  const [statusRating, setStatusRating] = useState(feedbackOnItem.status)
  const currentRatingState = statusRating

  const ratingStateConfig = getRatingStateConfig(currentRatingState, i18n, urlStaticsImages)

  const feedbackInput = useMemo(
    () =>
      new FeedbackInput({
        appName: config.get('APP_NAME'),
        baseUrl: `${API_URL}${TRUST_API_PATH}`,
        userToken: token
      }),
    [API_URL, TRUST_API_PATH, config, token]
  )

  const getRatingStatusTrackingData = useCallback(
    async currentRatingState => {
      const trackingData = await domain.get('get_page_tracking_data_use_case').execute({
        page: DEALER_RATING_STATE_PAGE_NAMES[currentRatingState]
      })

      return page(trackingData)
    },

    [domain, page, DEALER_RATING_STATE_PAGE_NAMES]
  )

  useEffect(() => {
    getRatingStatusTrackingData(currentRatingState)
  }, [currentRatingState, getRatingStatusTrackingData])

  const handleClickGoBackButton = useCallback(() => {
    router.goBack()
  }, [router])

  const handleEmptyState = useCallback(() => {
    router.push(ROUTES.ADS_LIST_SEARCH)
  }, [router])

  const getTrackingRatingSubmitError = useCallback(async () => {
    const scoreTrackingData = await domain.get('get_event_tracking_data_use_case').execute({
      event: TRACKING_EVENT_NAMES.USER_RATING_ERROR
    })

    track(scoreTrackingData)
  }, [domain, track, TRACKING_EVENT_NAMES])

  const handleOnSubmit = useCallback(
    async ({rating, options, comment}) => {
      if (showErrorRating) {
        getTrackingRatingSubmitError()
      }

      const answers = getAnswers({questions: feedbackOnItem.data?.questions, rating, options})

      try {
        await feedbackInput.submitAnswers({submissionUri: feedbackOnItem.submissionUri, textReview: comment, answers})
        setStatusRating(RATING_STATES.SUCCESS)
      } catch (error) {
        setShowErrorRating(true)
      }
    },
    [
      feedbackInput,
      feedbackOnItem.data?.questions,
      feedbackOnItem.submissionUri,
      getTrackingRatingSubmitError,
      showErrorRating
    ]
  )

  const handleOnSecondaryAction = () => {
    router.push(`${ROUTES.HOME}?${URL_PARAMS.TOKEN}=${token}&${URL_PARAMS.AD_ID}=${adId}`)
  }

  if (!feedbackOnItem) {
    return
  }

  return (
    <LayoutBasic.FullWidth
      linkFactory={LinkFactory}
      footerProps={{isSimple: true}}
      topbarProps={{
        customContent: <></>,
        mainNavigationItems: [],
        navButton: (
          <AtomButton
            aria-label="Volver atrás"
            design={atomButtonDesigns.FLAT}
            leftIcon={<IconAngleLeft />}
            onClick={handleClickGoBackButton}
            shape={atomButtonShapes.CIRCULAR}
            size={atomButtonSizes.LARGE}
          />
        ),
        showBrandIcon: true,
        showNavUserOnDesktop: false,
        showToggleNavOnMobile: false,
        title: 'Valora tu experiencia'
      }}
    >
      <LayoutBasic.Container isFullWidth isFullHeight isMarginlessOnMobile>
        <div className={BASE_CLASS_NAME}>
          {/* Temporary code until we implement the Ratings library to manage the Rating. */}
          {statusRating === RATING_STATES.PENDING ? (
            <FormDealerRating
              onSubmit={handleOnSubmit}
              onSecondaryAction={handleOnSecondaryAction}
              tradeId={tradeId}
              questions={feedbackOnItem.data?.questions}
              textReviewSettings={feedbackOnItem.data?.textReviewSettings}
            />
          ) : (
            <EmptyRatingState ctaAction={handleEmptyState} {...ratingStateConfig} />
          )}
        </div>
      </LayoutBasic.Container>
      {showErrorRating && (
        <AtomToast
          autoCloseTime={atomToastAutoCloseTimes.medium}
          iconClose={<IconClose size={atomIconSizes.medium} />}
          onClose={() => setShowErrorRating(false)}
          show={showErrorRating}
        >
          <div className={`${BASE_CLASS_NAME}-notification`}>
            <IconCloseCircle size="medium" />
            <p className={`${BASE_CLASS_NAME}-notificationText`}>
              {i18n.t('DEALER_RATINGS.STATES.ERROR.NOTIFICATION_TEXT')}
            </p>
          </div>
        </AtomToast>
      )}
    </LayoutBasic.FullWidth>
  )
}

Rating.displayName = 'Rating'

Rating.getInitialProps = getInitialProps

Rating.propTypes = {
  adId: PropTypes.string,
  feedbackOnItem: PropTypes.object,
  router: PropTypes.object,
  token: PropTypes.string,
  tradeId: PropTypes.string,
  __HTTP__: PropTypes.object
}

export default Rating
